import { useQuery } from "@apollo/client";
import { Theme } from "@emotion/react";
import { ALL_MEETINGS } from "apollo";
import { fakeMeetings } from "demo-data";
import { useDemoMode } from "hooks";
import { GetAllMeetingsResponse } from "types";
import { extractMeetingLocation, formatTimeToAM } from "utils/helpers";

type MeetingList = {
  id: string;
  title: string;
  label?: string;
  labelBgColor?: keyof Theme["colors"]["text"];
  type: "meeting" | "email";
  meetingId?: string;
  start?: number;
  end?: number;
  startAt?: string;
  endAt?: string;
  attendees?: {
    userName: string;
    source: string;
  }[];
  location?: string;
};

export const useMeeting = (dateSelected?: number) => {
  const { demoMode } = useDemoMode();
  //const {dateSelected} = useContext(SystemContext);

  const { data: meetingData, loading } = useQuery<GetAllMeetingsResponse>(
    ALL_MEETINGS,
    {
      //fetchPolicy: 'network-only',
      //pollInterval: demoMode ? undefined : 5000,
      variables: {
        date: dateSelected,
      },
    },
  );

  const externalMeetings = demoMode
    ? fakeMeetings
    : meetingData?.allMeetings?.externals || [];

  const allMeetings = externalMeetings
    .map(
      (item, idx) =>
        ({
          id: item._id || item.meetingId || (idx + 2).toString(),
          title: `${item.meetingName}`,
          label: "External",
          labelBgColor: "highlight/1" as keyof Theme["colors"]["text"],
          type: "meeting",
          meetingId: item.meetingId,
          start: new Date(item.startAt).getTime(),
          end: new Date(item.endAt).getTime(),
          startAt: formatTimeToAM(new Date(item.startAt)),
          endAt: formatTimeToAM(new Date(item.endAt)),
          attendees: item.attendees.map((attendee) => ({
            userName: attendee.user_full_name,
            source: attendee.profile_pic_url,
          })),
          location: extractMeetingLocation(item.location),
        }) as MeetingList,
    )
    .concat(
      (meetingData?.allMeetings?.internals || [])?.map((item, idx) => ({
        id: item.id || (idx + 2).toString(),
        title: `${item.eventTitle}`,
        label: (item.attendees?.length || 0) > 0 ? "Internal" : "Blocked time",
        labelBgColor: "seconday" as keyof Theme["colors"]["text"],
        type: "meeting",
        meetingId: item.id,
        start: new Date(item.start).getTime(),
        end: new Date(item.end).getTime(),
        startAt: formatTimeToAM(new Date(item.start)),
        endAt: formatTimeToAM(new Date(item.end)),
        attendees: item.attendees?.map((attendee) => ({
          userName: attendee.displayName || attendee.email,
          source: "",
        })),
        location: extractMeetingLocation(item.location || ""),
      })),
    )
    .sort((a, b) => (a.start || 0) - (b.start || 0)) as MeetingList[];

  return {
    loading,
    allMeetings,
    externalMeetings,
  };
};
